import { ISearchFormCustomFieldValue } from 'pages/LegalEntitiesPage/IndividualSearchForm';
import { ICustomFieldByObjectType } from './customField';
import { IPaginator } from './paginator';

export enum ELicenceType {
  ONLINE = 'ONLINE',
  OFFLINE = 'OFFLINE',
}

export interface ILicenceData {
  licenceId: string;
  licenceType: ELicenceType;
  startDate: string;
  expiryDate: string;
  numberOfUsers: number;
  currentSystemUsers: number;
  includeWeaponLicenceModule: boolean;
}

export interface ILicencePrintout {
  weaponLicenceUniqueId: string;
  printUniqueId: string;
  status: string;
  statusUniqueId: string;
  typeUniqueId: string;
  description: string;
  userUniqueId: string;
  userId: string;
  user: string;
  createdDate: string;
}

export interface ILicencePrintoutsHistoryResponse extends Array<ILicencePrintout> {}

export interface ILicencePrintAvailability {
  canBePrinted: boolean;
}

export interface ILicenceBasicInfo {
  licenceType: string;
  expiryDate: string | null;
  includeWeaponLicenceModule: boolean;
}

export interface IDocument {
  name: string;
  weaponLicenceDocumentType: string;
  comment: string;
}

export interface IOwnerIndividual {
  firstName: string | null;
  middleNames: string | null;
  lastName: string | null;
  genderUniqueId: string | null;
  dateOfBirth: string | null;
  placeOfBirth: string | null;
  nationalityUniqueId: string | null;
  height: string | null;
  occupation: string | null;
  secondSurname: string | null;
  identityDocumentTypeUniqueId?: string | null;
  identityDocumentNumber?: string | null;
  prefixValueUniqueId: string | null;
}
export interface IOwnerOrganisation {
  organisationName: string | null;
  departmentName: string | null;
  organisationCategoryUniqueId: string | null;
  organisationTypeUniqueIds: string[] | null;
}
interface IPrimaryAddress {
  typeUniqueId: string;
  line1: string;
  line2: string;
  city: string;
  postalCode: string;
  regionUniqueId: string;
  countryUniqueId: string;
  latitude: number | string;
  longitude: number | string;
  storage: boolean;
  addressUniqueId: string;
}

export interface IOwner {
  legalUniqueId: string;
  individual?: IOwnerIndividual;
  organisation?: IOwnerOrganisation;
  primaryAddress: IPrimaryAddress | null;
  displayUniqueId: string;
}
export interface ISafeStorage {
  addressUniqueId: string;
  typeUniqueId: string;
  line1: string;
  line2: string;
  city: string;
  postalCode: string;
  regionUniqueId: string;
  countryUniqueId: string;
  latitude: number;
  longitude: number;
  storage: boolean;
  isDefault: boolean;
}
export interface ILegalEntityWeaponLicence {
  weaponLicenceTemplateUniqueId: string;
  general: {
    name: string;
    code: string;
    startDate: string;
    expiryDate: string | null;
  };
  conditions: {
    weaponNumberLimit: number | null;
    goodReasonUniqueIds: string[] | null;
    goodReason: string | null;
    areaOfValidityUniqueId?: string | null;
    competencyUniqueId: string | null;
    weaponCategorisationUniqueId: string | null;
    carry: string | null;
    additionalConditions: string | null;
    hasSafeStorage: boolean | string | null;
    safeStorageDescription: string | null;
    safeStorages: ISafeStorage[] | null;
  };
  documents: IDocument[];
  additionalInformation: string | null;
  owner: IOwner;
}
export interface ILegalEntityWeaponLicenceVary {
  conditions: {
    weaponNumberLimit: number | null;
    goodReasonUniqueIds: string[] | null;
    goodReason: string;
    areaOfValidityUniqueId?: string | null;
    competencyUniqueId: string | null;
    hasSafeStorage: boolean | string | null;
    safeStorageDescription: string | null;
    safeStorages: any[] | null;
    weaponCategorisationUniqueId: string | null;
    carry: string | null;
    additionalConditions: string | null;
  };
  firearm: {
    firearmToLinkUniqueIds: string[];
    firearmLinkToBeRemovedUniqueIds: string[];
  };
  additionalInformation: string | null;
  document: {
    documentToRemoveUniqueIds: string[];
    documents: IDocument[];
  };
  version: string | number;
  shouldUpdateScheduled: boolean;
  owner: IOwner;
}

export interface ILegalEntityWeaponLicenceRenew {
  conditions: {
    weaponNumberLimit: number | null;
    goodReasonUniqueIds: string[] | null;
    goodReason: string;
    areaOfValidityUniqueId?: string | null;
    competencyUniqueId: string | null;
    hasSafeStorage: boolean | string | null;
    safeStorageDescription: string | null;
    safeStorages: any[] | null;
    weaponCategorisationUniqueId: string | null;
    carry: string | null;
    additionalConditions: string | null;
  };
  firearm: {
    firearmLinkToBeRemovedUniqueIds: string[];
  };
  documents: IDocument[];
  additionalInformation: string | null;
  version: string | number;
  owner: IOwner;
}

export interface ILicenceAttachment {
  uniqueId: string;
  name: string;
  type: string;
  fileType: string;
  createdDate: string;
  size: number;
  weaponLicenceDocumentType: string;
  comment: string;
  addedBy: string;
}

export interface ILicenceAttachmentsResponse extends IPaginator {
  content: ILicenceAttachment[];
}

export interface ILicenceActionRequest {
  internalReason?: string;
  externalReason?: string;
}

export interface ILicenceHistoryItem {
  uniqueId: string;
  licenceStatusUniqueId: string;
  historyStatus: string;
  description: string;
  createdBy: string;
  createdByName: string;
  auditLogUniqueId: string;
  fee: string;
  createdDate: string;
  internalReason: string;
  externalReason: string;
}

export interface ILicenceHistoryDetails {
  uniqueId: string;
  historyStatus: string;
  licenceStatusUniqueId: string;
  description: string;
  createdBy: string;
  createdByName: string;
  auditLogUniqueId: string;
  post: any;
  previous: any;
  createdDate: string;
}

export interface ILicenceHistoryResponse extends Array<ILicenceHistoryItem> {}

export interface ISearchFormValues {
  preset?: string;
  weaponLicenceName: string;
  weaponLicenceNumber: string;
  legalName: string;
  legalUniqueId: string;
  legalTypeUniqueId: string;
  statusesUniqueIds: string[];
  expiryDateFrom: string | null;
  expiryDateTo: string | null;
  licenceAuthorityUniqueIds: string[];
  invokedByPageChange?: boolean;
}

export interface ILicenceUpdatePaymentStatusRequest {
  paymentStatusUniqueId?: string;
}

export interface ILicenceScheduledResponse {
  uniqueId: string;
  actualWeaponAmount: number;
  weaponNumberLimit: number;
}

export interface IRenewRequest {
  request: {
    legalUniqueId: string;
    licenceAuthorityUniqueId: string;
    weaponLicenceUniqueId: string;
    paymentStatusUniqueId: string;
    documentTemplateUniqueIds: string[];
    documentsFilenames: string[];
  };
  applicationFiles: File[] | null;
  documents: File[] | null;
}
export interface IVaryRequest {
  request: {
    legalUniqueId: string;
    licenceAuthorityUniqueId: string;
    weaponLicenceUniqueId: string;
    paymentStatusUniqueId: string;
    description: string;
    applicationProcessUniqueId: string;
    documentTemplateUniqueIds: string[];
  };
  documents: File[] | null;
}

export interface IRenewData {
  legalEntity: {
    legalName: string;
    primaryLicenceAuthorityUniqueId: string;
    secondaryLicenceAuthorityUniqueId: string;
  };
  licence: {
    licenceCode: string;
    licenceName: string;
    licenceNumber: string;
    internalDescription: string;
    endDate: string;
    startDate: string;
    licenceAuthorityUniqueId: string;
  };
  processTypeUniqueId: string;
  licenceTemplateDocuments: {
    uniqueId: string;
    typeUniqueId: string;
    importantNotice: string;
    applicationProcessId: string;
  }[];
}
export interface IVaryDataVariationsDocuments {
  uniqueId: string;
  typeUniqueId: string;
  importantNotice: string;
}
export interface IVaryDataVariations {
  uniqueId: string;
  name: string;
  fee: any;
  description: string;
  documents: IVaryDataVariationsDocuments[];
}
export interface IVaryData {
  legalEntity: {
    legalName: string;
    primaryLicenceAuthorityUniqueId: string;
    secondaryLicenceAuthorityUniqueId: string;
  };
  processTypeUniqueId: string;
  licence: {
    licenceCode: string;
    licenceName: string;
    licenceNumber: string;
    internalDescription: string;
    endDate: string;
    startDate: string;
    licenceAuthorityUniqueId: string;
  };
  variations: IVaryDataVariations[];
}
